<script setup lang="ts">
const requestUrl = useRequestURL()
const i18n = useI18n()
const language = requestUrl.pathname.split('/')[1]
const slug =
  requestUrl.pathname.split('/')[3] !== 'test'
    ? requestUrl.pathname.split('/')[3]
    : requestUrl.pathname.split('/')[4]

let deal: Deal | undefined

const shouldPrerenderBody =
  requestUrl.pathname.endsWith(slug) || requestUrl.pathname.endsWith(slug + '/')

function stripTags(html?: string): string {
  try {
    return (html ?? '').replace(/<[^>]*>?/gm, '')
  } catch (error) {
    console.error(error)
    return ''
  }
}

try {
  if (!slug || slug === 'test') {
    throw new Error('No slug provided or slug is "test"')
  }

  const path =
    'https://api.outspot.be/v2/public/deals/slug/' +
    slug +
    '?language=' +
    language

  const { data } = await useAsyncData(path.split('?')[0], () => $fetch(path))

  // @ts-ignore
  deal = data.value?.data as Deal

  useSeoMeta({
    title: deal
      ? stripTags(deal.shortName) + ' — ' + i18n.t('meta.title')
      : i18n.t('meta.title'),
    ogTitle: stripTags(deal.shortName),
    description: stripTags(deal.description),
    ogDescription: stripTags(deal?.description).substring(0, 200) + '…',
    ogImage: deal?.images[0]?.xl,
    ogUrl: requestUrl.href,
    twitterCard: 'summary_large_image',
    twitterTitle: stripTags(deal?.shortName),
    twitterDescription: stripTags(deal?.description).substring(0, 200) + '…',
    twitterImage: deal?.images[0]?.xl,
  })
} catch (error) {}

useHead({
  script: () => [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org/',
        '@graph': (deal?.variants ?? []).map((variant: Variant) => ({
          '@type': 'Product',
          name: stripTags(deal?.name + ' — ' + variant.name),
          image: [
            variant.image?.xl,
            ...(deal?.images ?? []).map((image: Image) => image.xl),
          ].filter((imageSrc) => !!imageSrc),
          description: stripTags(deal?.description).substring(0, 200) + '…',
          offers: {
            '@type': 'Offer',
            priceCurrency: i18n.t('language.currency'),
            itemCondition: 'https://schema.org/NewCondition',
            availability: 'https://schema.org/InStock',
            price: variant.discountPrice,
            priceSpecification: [
              {
                '@type': 'UnitPriceSpecification',
                priceType: 'https://schema.org/ListPrice',
                price: variant.originalPrice,
              },
              {
                '@type': 'UnitPriceSpecification',
                priceType: 'https://schema.org/SalePrice',
                price: variant.discountPrice,
                validFrom: deal?.startDate,
                validThrough: deal?.endDate,
              },
            ],
          },
        })),
      }),
      // This disables Vue's HTML escaping for this script
      tagPosition: 'head',
      key: 'ldjson',
      // @ts-ignore
      'data-hid': 'ldjson',
    },
  ],
})
</script>

<template>
  <div class="prerendered" v-if="deal && shouldPrerenderBody">
    <h1>{{ deal?.shortName }}</h1>
    <h2>{{ deal?.name }}</h2>
    <div v-html="deal?.highlights"></div>
    <div v-html="deal?.description"></div>
    <div v-html="deal?.practical"></div>
    <NuxtImg
      format="webp"
      v-for="image in deal?.images"
      :src="convertJpgToWebPIfSupported(image.xl, true)"
      class="deal-image-ratio"
      :alt="deal?.shortName"
    />
  </div>
</template>

<style scoped>
.prerendered {
  position: fixed;
  top: -9999px;
  left: -9999px;
  width: 100px;
  height: 100px;
}
</style>
